import { PageHeader } from '../design-system/primitives';
import { useMemo, useState, type CSSProperties } from 'react';
import type { ExperienceConfiguration, ThemeCatalog, ThemeTokenSet } from '@ayumia/contracts';
import { ApiError } from '../api/client';
import { text } from '../app/i18n';
import { Empty, errorView, Loading } from '../states/States';
import { applyThemes } from '../theme/theme';
import { useLoad } from '../views/useLoad';
import { useAppServices } from '../views/ViewContext';
import './themes.css';

/**
 * Thèmes du tenant (ADR-092 §3). Le catalogue vient de `ListThemes` ; les thèmes de paquet (design
 * system) sont immuables, le tenant en dérive une couche customer. L'édition ne touche que des
 * jetons de la nomenclature, en valeurs contrôlées (couleur, police d'une liste fermée) ; l'aperçu
 * applique les variables au seul conteneur d'aperçu et ne publie rien. Publier, choisir ou revenir
 * à une version passe par une commande : le serveur autorise (`Theme:administer`), valide et
 * signale les contrastes insuffisants. Masquer un bouton n'autorise rien.
 */

type CatalogTheme = ThemeCatalog['themes'][number];
type CatalogVersion = CatalogTheme['versions'][number];

/** Jetons éditables, par groupe ; les polices se choisissent dans une liste fermée. */
export const TOKEN_GROUPS: ReadonlyArray<{ title: string; paths: readonly string[] }> = [
  { title: 'Fond et surfaces', paths: ['color.surface.canvas', 'color.surface.default', 'color.surface.muted', 'color.surface.sunken', 'color.border.default', 'color.border.strong', 'color.shell.background'] },
  { title: 'Encre', paths: ['color.text.primary', 'color.text.secondary', 'color.text.muted', 'color.text.inverse'] },
  { title: 'Accent et actions', paths: ['color.action.primary', 'color.action.primary_hover', 'color.action.on_primary', 'color.action.secondary', 'color.action.on_secondary', 'color.accent.default', 'color.accent.soft', 'color.focus.ring'] },
  {
    title: 'Rail et barre du haut',
    paths: ['color.shell.rail', 'color.shell.rail_text', 'color.shell.rail_text_hover', 'color.shell.rail_active', 'color.shell.rail_active_text', 'color.shell.rail_indicator', 'color.shell.topbar', 'color.shell.topbar_text', 'color.shell.topbar_muted', 'color.shell.badge', 'color.shell.badge_text', 'color.shell.avatar', 'color.shell.avatar_text'],
  },
  { title: 'Tiroir', paths: ['color.shell.drawer', 'color.shell.drawer_text', 'color.shell.drawer_header', 'color.shell.drawer_header_text', 'color.shell.drawer_section', 'color.shell.drawer_active', 'color.shell.drawer_active_text'] },
  { title: 'États', paths: ['color.status.success', 'color.status.success_soft', 'color.status.warning', 'color.status.warning_soft', 'color.status.danger', 'color.status.danger_soft', 'color.status.info'] },
];

/** Piles de polices admises (miroir de la liste fermée du serveur). */
export const FONT_STACKS: ReadonlyArray<{ label: string; value: string }> = [
  { label: 'Inter', value: 'Inter, system-ui, sans-serif' },
  { label: 'Fraunces', value: 'Fraunces, Georgia, serif' },
  { label: 'JetBrains Mono', value: '"JetBrains Mono", ui-monospace, monospace' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Système', value: 'system-ui, sans-serif' },
];
const FONT_PATHS: ReadonlyArray<{ path: string; label: string }> = [
  { path: 'typography.family', label: 'Texte' },
  { path: 'typography.family_display', label: 'Titres' },
  { path: 'typography.family_mono', label: 'Codes' },
];

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FUNCTIONAL = /^(?:rgba?|hsla?)\(\s*-?\d+(?:\.\d+)?%?(?:deg)?(?:\s*,\s*-?\d+(?:\.\d+)?%?){2}(?:\s*,\s*-?\d+(?:\.\d+)?%?)?\s*\)$|^(?:rgba?|hsla?)\(\s*-?\d+(?:\.\d+)?%?(?:deg)?(?:\s+-?\d+(?:\.\d+)?%?){2}(?:\s*\/\s*-?\d+(?:\.\d+)?%?)?\s*\)$/i;

/** Couleur admise : hexadécimal, `rgb()`, `rgba()`, `hsl()`, `hsla()` ; aucun CSS libre. */
export function isColor(value: string): boolean {
  const trimmed = value.trim();
  return HEX.test(trimmed) || FUNCTIONAL.test(trimmed);
}

/** Valeur hexadécimale à 6 chiffres pour `<input type="color">`, ou `null`. */
function hex6(value: string): string | null {
  const trimmed = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(trimmed)) return trimmed;
  if (/^#[0-9a-f]{3}$/.test(trimmed)) return `#${[...trimmed.slice(1)].map((digit) => digit + digit).join('')}`;
  if (/^#[0-9a-f]{8}$/.test(trimmed)) return trimmed.slice(0, 7);
  return null;
}

function latestOf(theme: CatalogTheme): CatalogVersion {
  return theme.versions[0]!;
}

/** Chaîne de thèmes d'une version : la couche, puis la version exacte du thème dont elle dérive. */
function chainOf(catalog: ThemeCatalog, body: ThemeTokenSet): ThemeTokenSet[] {
  const chain: ThemeTokenSet[] = [body];
  const source = body.derived_from;
  if (source !== undefined) {
    const parent = catalog.themes.find((theme) => theme.artifact_id === source.artifact_id)?.versions.find((version) => version.artifact_version === source.artifact_version);
    if (parent !== undefined) chain.push(parent.body);
  }
  return chain;
}

/** Valeurs effectives (jeton → valeur) d'une chaîne, thème par défaut du front compris. */
function resolved(chain: readonly ThemeTokenSet[]): Record<string, string | number> {
  return applyThemes(chain).tokens;
}

function Swatches({ tokens }: { tokens: Record<string, string | number> }) {
  const shown = ['color.surface.canvas', 'color.shell.rail', 'color.action.primary', 'color.accent.default', 'color.status.warning', 'color.text.primary'];
  return (
    <span className="ay-themes__swatches" aria-hidden="true">
      {shown.map((path) => (
        <span key={path} className="ay-themes__swatch" style={{ background: String(tokens[path] ?? 'transparent') }} />
      ))}
    </span>
  );
}

/** Mini-maquette rail / barre / carte, rendue avec les seules variables du thème montré. */
export function ThemePreview({ chain, label = 'Aperçu' }: { chain: readonly ThemeTokenSet[]; label?: string }) {
  const style = useMemo(() => applyThemes(chain).variables as CSSProperties, [chain]);
  return (
    <figure className="ay-themes__preview" style={style} aria-label={label} data-testid="theme-preview">
      <div className="ay-themes__preview-rail">
        <span className="ay-themes__preview-logo">A</span>
        <span className="ay-themes__preview-icon ay-themes__preview-icon--active" />
        <span className="ay-themes__preview-icon" />
        <span className="ay-themes__preview-icon" />
      </div>
      <div className="ay-themes__preview-body">
        <div className="ay-themes__preview-topbar">
          <span>AYUMIA | Tenant</span>
          <span className="ay-themes__preview-avatar">PV</span>
        </div>
        <div className="ay-themes__preview-canvas">
          <div className="ay-themes__preview-card">
            <strong className="ay-themes__preview-title">Carte</strong>
            <span className="ay-themes__preview-muted">Texte secondaire</span>
            <span className="ay-themes__preview-actions">
              <span className="ay-themes__preview-primary">Action</span>
              <span className="ay-themes__preview-warning">Alerte</span>
            </span>
          </div>
        </div>
      </div>
    </figure>
  );
}

interface Draft {
  /** Thème customer à faire évoluer ; absent : nouveau thème du tenant (`theme_slug`). */
  theme_ref?: string;
  slug: string;
  label: string;
  derived_from: { artifact_id: string; artifact_version: string };
  overrides: Record<string, string>;
  expected_latest_version?: string;
}

function ThemeEditor({ catalog, draft, onClose, onPublished }: { catalog: ThemeCatalog; draft: Draft; onClose: () => void; onPublished: (message: string) => void }) {
  const services = useAppServices();
  const [state, setState] = useState<Draft>(draft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const source = catalog.themes.find((theme) => theme.artifact_id === state.derived_from.artifact_id)?.versions.find((version) => version.artifact_version === state.derived_from.artifact_version);
  const overlay = useMemo<ThemeTokenSet>(() => {
    const semantic: ThemeTokenSet['semantic'] = {};
    for (const [path, value] of Object.entries(state.overrides)) if (value.trim() !== '' && (path.startsWith('typography.') || isColor(value))) semantic[path] = { value: value.trim() };
    return { label: { fr: state.label }, semantic };
  }, [state.overrides, state.label]);
  const chain = useMemo(() => (source === undefined ? [overlay] : [overlay, source.body]), [overlay, source]);
  const effective = useMemo(() => resolved(chain), [chain]);
  const set = (path: string, value: string): void => setState((current) => ({ ...current, overrides: { ...current.overrides, [path]: value } }));
  const invalid = Object.entries(state.overrides).filter(([path, value]) => !path.startsWith('typography.') && value.trim() !== '' && !isColor(value)).map(([path]) => path);

  const publish = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setFieldErrors({});
    const payload: Record<string, unknown> = { label: { fr: state.label }, derived_from: state.derived_from, semantic: overlay.semantic, reason: 'Thème modifié depuis l’écran Thèmes.' };
    if (state.theme_ref !== undefined) payload['theme_ref'] = state.theme_ref;
    else payload['theme_slug'] = state.slug;
    if (state.expected_latest_version !== undefined) payload['expected_latest_version'] = state.expected_latest_version;
    try {
      const response = await services.client.command<{ result?: { artifact_id?: string; artifact_version?: string; warnings?: Array<{ text: string; background: string; ratio: number }> } }>('PublishTheme', { payload });
      const warnings = response.result?.warnings ?? [];
      onPublished(
        `Thème publié : ${response.result?.artifact_id ?? ''} ${response.result?.artifact_version ?? ''}.` +
          (warnings.length === 0 ? '' : ` Contraste insuffisant (WCAG AA) : ${warnings.map((warning) => `${warning.text} sur ${warning.background} (${warning.ratio}:1)`).join(' ; ')}.`),
      );
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(caught.message);
        setFieldErrors(Object.fromEntries(caught.fieldErrors.map((entry) => [entry.field.replace(/^semantic\./, ''), entry.message])));
      } else setError(String(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ay-card ay-themes__editor" aria-labelledby="ay-theme-editor-title">
      <header className="ay-stack">
        <h2 id="ay-theme-editor-title">{state.theme_ref === undefined ? 'Nouveau thème' : `Modifier ${state.theme_ref}`}</h2>
        <span className="ay-text ay-text--muted">
          Dérivé de {state.derived_from.artifact_id}@{state.derived_from.artifact_version}
        </span>
      </header>
      <div className="ay-themes__editor-layout">
        <form
          className="ay-stack ay-stack--column"
          onSubmit={(event) => {
            event.preventDefault();
            void publish();
          }}
        >
          <label className="ay-field">
            <span className="ay-field__label">Nom du thème</span>
            <input value={state.label} onChange={(event) => setState({ ...state, label: event.target.value })} required />
          </label>
          {state.theme_ref === undefined && (
            <label className="ay-field">
              <span className="ay-field__label">Identifiant (custom.theme.…)</span>
              <input value={state.slug} pattern="[a-z][a-z0-9_]{0,40}" onChange={(event) => setState({ ...state, slug: event.target.value })} required />
            </label>
          )}
          {TOKEN_GROUPS.map((group) => (
            <fieldset key={group.title} className="ay-themes__group">
              <legend>{group.title}</legend>
              {group.paths.map((path) => {
                const value = state.overrides[path] ?? '';
                const current = String(effective[path] ?? '');
                const picker = hex6(value === '' ? current : value);
                return (
                  <div key={path} className="ay-themes__token">
                    <span className="ay-themes__token-name">{path.replace(/^color\./, '')}</span>
                    <input type="color" aria-label={`${path} (sélecteur)`} value={picker ?? '#000000'} disabled={picker === null && value !== ''} onChange={(event) => set(path, event.target.value)} />
                    <input aria-label={path} value={value} placeholder={current} aria-invalid={invalid.includes(path) || fieldErrors[path] !== undefined} onChange={(event) => set(path, event.target.value)} />
                    {value !== '' && (
                      <button type="button" className="ay-button ay-button--secondary" aria-label={`Rétablir ${path}`} onClick={() => setState((currentState) => ({ ...currentState, overrides: Object.fromEntries(Object.entries(currentState.overrides).filter(([key]) => key !== path)) }))}>
                        ↺
                      </button>
                    )}
                    {(invalid.includes(path) || fieldErrors[path] !== undefined) && <span className="ay-field__error">{fieldErrors[path] ?? 'Couleur attendue : #rrggbb, rgb(), rgba(), hsl().'}</span>}
                  </div>
                );
              })}
            </fieldset>
          ))}
          <fieldset className="ay-themes__group">
            <legend>Polices</legend>
            {FONT_PATHS.map(({ path, label }) => (
              <label key={path} className="ay-field ay-field--inline">
                <span className="ay-field__label">{label}</span>
                <select value={state.overrides[path] ?? ''} onChange={(event) => set(path, event.target.value)}>
                  <option value="">Comme le thème source</option>
                  {FONT_STACKS.map((stack) => (
                    <option key={stack.value} value={stack.value}>
                      {stack.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
          </fieldset>
          {error !== null && (
            <p role="alert" className="ay-field__error">
              {error}
            </p>
          )}
          <span className="ay-stack">
            <button type="submit" className="ay-button" disabled={busy || invalid.length > 0 || state.label.trim() === ''}>
              Publier
            </button>
            <button type="button" className="ay-button ay-button--secondary" onClick={onClose}>
              Annuler
            </button>
          </span>
        </form>
        <div className="ay-themes__live">
          <h3>Aperçu en direct</h3>
          <p className="ay-text ay-text--muted">Rien n’est publié tant que vous n’avez pas cliqué sur « Publier ».</p>
          <ThemePreview chain={chain} label="Aperçu en direct" />
        </div>
      </div>
    </section>
  );
}

function slugFor(base: string, catalog: ThemeCatalog): string {
  const root = base.split('.').pop()!.replace(/[^a-z0-9_]/g, '_');
  let candidate = `${root}_copie`;
  for (let index = 2; catalog.themes.some((theme) => theme.artifact_id === `custom.theme.${candidate}`); index += 1) candidate = `${root}_copie_${index}`;
  return candidate;
}

/**
 * Couche de sélection : « Utiliser ce thème » sur un thème de paquet publie une couche customer sans
 * surcharge (ADR-092 §3, le tenant n'a qu'un thème actif, toujours le sien). Ce n'est pas un thème du
 * tenant : l'écran la fond dans la carte du paquet dont elle dérive, qui porte alors « Actif ».
 */
export function isSelectionLayer(catalog: ThemeCatalog, theme: CatalogTheme): boolean {
  if (!theme.editable) return false;
  const latest = latestOf(theme);
  const source = latest.body.derived_from;
  if (source === undefined) return false;
  const parent = catalog.themes.find((candidate) => candidate.artifact_id === source.artifact_id);
  if (parent === undefined || parent.editable) return false;
  // Une marque (nom de produit, logo) fait un vrai thème, même sans jeton surchargé.
  if (latest.body.brand !== undefined) return false;
  return Object.values(latest.body.semantic).every((entry) => entry.value === undefined && entry.ref === undefined) && Object.keys(latest.body.primitives ?? {}).length === 0;
}

function overridesOf(body: ThemeTokenSet): Record<string, string> {
  return Object.fromEntries(Object.entries(body.semantic).filter(([, entry]) => entry.value !== undefined).map(([path, entry]) => [path, String(entry.value)]));
}

export function ThemesPage() {
  const services = useAppServices();
  const [loaded, reload] = useLoad<ThemeCatalog>('themes', async () => (await services.client.query<ThemeCatalog>('ListThemes')).data);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<string | null>(null);

  /** Relit la chaîne servie par `GetExperience` et l'applique sur place : le choix se voit tout de suite. */
  const refreshTheme = async (): Promise<void> => {
    if (services.applyThemeChain === undefined) return;
    const experience = (await services.client.query<ExperienceConfiguration>('GetExperience')).data;
    services.applyThemeChain(experience.themes.map((entry) => entry.body));
  };

  if (loaded.status === 'loading') return <Loading />;
  if (loaded.status === 'error') return <>{errorView(loaded.error, reload)}</>;
  const catalog = loaded.value;
  if (catalog.themes.length === 0) return <Empty label="Aucun thème installé : installer un paquet de design system." />;

  const select = async (artifactId: string, artifactVersion: string): Promise<void> => {
    setError(null);
    try {
      await services.client.command('SelectTheme', { payload: { artifact_id: artifactId, artifact_version: artifactVersion, reason: 'Choix depuis l’écran Thèmes.' } });
      const chosen = catalog.themes.find((theme) => theme.artifact_id === artifactId);
      setMessage(`Thème actif : ${chosen === undefined ? artifactId : text(chosen.label)} (${artifactVersion}).`);
      reload();
      await refreshTheme();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : String(caught));
    }
  };

  const activeTheme = catalog.themes.find((theme) => theme.artifact_id === catalog.active_theme?.artifact_id);
  const activeVersion = activeTheme?.versions.find((version) => version.artifact_version === catalog.active_theme?.artifact_version);
  // Couche de sélection active : le thème montré actif est le paquet dont elle dérive, dans la version qu'elle cite.
  const activeRef = activeTheme !== undefined && activeVersion !== undefined && isSelectionLayer(catalog, activeTheme) && activeVersion.body.derived_from !== undefined ? activeVersion.body.derived_from : catalog.active_theme;
  const activeId = activeRef?.artifact_id;
  const shown = catalog.themes.filter((theme) => !isSelectionLayer(catalog, theme));
  return (
    <section className="ay-themes" aria-labelledby="ay-themes-title">
      <PageHeader titleId="ay-themes-title" title="Thèmes" subtitle="Un thème par tenant, publié et choisi par commande ; il remplace toute la palette sans recompiler." />
      <header className="ay-stack">
        {message !== null && (
          <span role="status" className="ay-themes__message">
            {message}
          </span>
        )}
      </header>
      {error !== null && (
        <p role="alert" className="ay-field__error">
          {error}
        </p>
      )}
      <ul className="ay-themes__list">
        {shown.map((theme) => {
          const latest = latestOf(theme);
          const chain = chainOf(catalog, latest.body);
          const tokens = resolved(chain);
          const active = theme.artifact_id === activeId;
          const derived = latest.derived_from ?? (theme.editable ? undefined : { artifact_id: theme.artifact_id, artifact_version: latest.artifact_version });
          return (
            <li key={theme.artifact_id} className={`ay-card ay-themes__item${active ? ' ay-card--selected' : ''}`} aria-current={active ? 'true' : undefined}>
              <header className="ay-stack">
                <strong>{text(theme.label)}</strong>
                <span className="ay-badge">{theme.editable ? 'Thème du tenant' : 'Paquet'}</span>
                {active && <span className="ay-badge ay-badge--success">{activeRef?.artifact_version === latest.artifact_version ? 'Actif' : `Actif (${activeRef?.artifact_version ?? ''})`}</span>}
                <Swatches tokens={tokens} />
              </header>
              <span className="ay-text ay-text--muted">
                {theme.artifact_id}@{latest.artifact_version}
                {latest.derived_from === undefined ? '' : ` — dérivé de ${latest.derived_from.artifact_id}@${latest.derived_from.artifact_version}`}
              </span>
              <ThemePreview chain={chain} label={`Aperçu ${text(theme.label)}`} />
              <span className="ay-stack">
                {!(active && activeRef?.artifact_version === latest.artifact_version) && (
                  <button type="button" className="ay-button" onClick={() => void select(theme.artifact_id, latest.artifact_version)}>
                    {active ? `Passer à ${latest.artifact_version}` : 'Utiliser ce thème'}
                  </button>
                )}
                {theme.editable && derived !== undefined && (
                  <button type="button" className="ay-button ay-button--secondary" onClick={() => setDraft({ theme_ref: theme.artifact_id, slug: '', label: text(theme.label), derived_from: derived, overrides: overridesOf(latest.body), expected_latest_version: latest.artifact_version })}>
                    Modifier
                  </button>
                )}
                {derived !== undefined && (
                  <button type="button" className="ay-button ay-button--secondary" onClick={() => setDraft({ slug: slugFor(theme.artifact_id, catalog), label: `${text(theme.label)} (copie)`, derived_from: derived, overrides: theme.editable ? overridesOf(latest.body) : {} })}>
                    {theme.editable ? 'Dupliquer' : 'Personnaliser'}
                  </button>
                )}
                {theme.editable && theme.versions.length > 1 && (
                  <button type="button" className="ay-button ay-button--secondary" aria-expanded={history === theme.artifact_id} onClick={() => setHistory(history === theme.artifact_id ? null : theme.artifact_id)}>
                    Versions
                  </button>
                )}
              </span>
              {history === theme.artifact_id && (
                <ol className="ay-themes__versions" aria-label={`Versions de ${theme.artifact_id}`}>
                  {theme.versions.map((version) => (
                    <li key={version.artifact_version}>
                      v{version.artifact_version}
                      {version.published_at === undefined ? '' : ` — ${new Date(version.published_at).toLocaleString('fr-FR')}`}
                      <code className="ay-themes__hash" title={version.content_hash}>
                        {version.content_hash.slice(0, 15)}…
                      </code>
                      {version.active ? (
                        <span className="ay-badge ay-badge--success">Active</span>
                      ) : (
                        <button type="button" className="ay-button ay-button--secondary" onClick={() => void select(theme.artifact_id, version.artifact_version)}>
                          Revenir à cette version
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </li>
          );
        })}
      </ul>
      {draft !== null && (
        <ThemeEditor
          key={`${draft.theme_ref ?? draft.slug}`}
          catalog={catalog}
          draft={draft}
          onClose={() => setDraft(null)}
          onPublished={(published) => {
            setDraft(null);
            setMessage(published);
            reload();
            void refreshTheme();
          }}
        />
      )}
    </section>
  );
}
